/*
 * Copyright (c) 2024 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext } from "react";
import { DataContext } from "../../Context";
import { Button, Form, Input, Label } from "reactstrap";
import { localizations } from "../../util/Localization";
import { useUpdateState } from "../../util/State";
import { formatDate, monthList } from "../../util/DateTime";
import CenterModal from "../../elements/modal/CenteredModal";
import GridContainer from "../../elements/containers/GridContainer";
import timezoneList from "../../util/TimezoneList";

const ModalDataUpdader = ({
  isOpen,
  onSubmit,
  onDone,
}) => {
  // context
  const {
    name, 
    email,
    emailStatic = false,
    phoneNumber,
    smsStatic = false,
    birthday,
    language,
    timezone,
  } = useContext(DataContext);

  // state
  const initialContext = {
    name: name,
    email: email,
    phoneNumber: phoneNumber,
    birthDay: birthday?.day?.toString(),
    birthMonth: birthday?.month?.toString(),
    birthYear: birthday?.year?.toString(),
    language: language,
    timezone: timezone,
  };
  const [formState, updateFormState] = useUpdateState(initialContext);

  // renderers
  const updateInput = (key, {title, options, disabled}) => (
    <div className="d-flex flex-column">
      {title && <Label className="no-spacing">{title}</Label>}
      <Input
        name={key}
        disabled={disabled}
        type={options == null ? "text" : "select"}
        defaultValue={formState[key]}
        onChange={e => updateFormState({ [key]: e.target.value })}
      >
        {options != null ? (
          <>
            <option value=""></option>
            {options}
          </>
        ) : null}
      </Input>
    </div>
  );

  const dateInput = () => {
    // year selection
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 121 }, (_, index) => currentYear - index);
    const yearOptions = years.map(year => <option key={year} value={year}>{year}</option>)

    // month selection
    const monthOptions = monthList.map(({value, name}) => <option key={value} value={value}>{name}</option>)

    // day selection
    const currentMonth = monthList.find(month => month.value === formState.birthMonth);
    const dayCount = currentMonth?.days ?? 31;
    const days = Array.from({ length: dayCount }, (_, index) => index + 1);
    const dayOptions = days.map(day => <option key={day} value={day}>{day}</option>)

    return (
      <div className="d-flex" style={{ flexDirection: "column" }}>
        <Label className="no-spacing">Birthday</Label>
        <div className="d-flex justify-content-between">
          <div style={{ flex: "0 0 30%"}}>
            {updateInput("birthYear", { options: yearOptions })}
          </div>
          <div style={{ flex: "0 0 35%"}}>
            {updateInput("birthMonth", { options: monthOptions })}
          </div>
          <div style={{ flex: "0 0 25%"}}>
            {updateInput("birthDay", { options: dayOptions })}
          </div>
        </div>
      </div>
    );
  };

  // handlers
  const handleSubmit = (event) => {
    event.preventDefault();

    // parse data
    const data = {
      name: formState.name,
      email: emailStatic ? null : formState.email,
      phoneNumber: smsStatic ? null : formState.phoneNumber,
      birthday: formatDate(formState.birthDay, formState.birthMonth, formState.birthYear),
      language: formState.language,
      timezone: formState.timezone,
    };

    // handle callbacks
    onSubmit?.(data);
    onDone?.();
  };

  const handleCancel = () => {
    onDone?.();
  }

  return (
    <CenterModal isOpen={isOpen} toggle={() => {}} size="lg">
      <Form onSubmit={handleSubmit}>
        <Label className="w-100 text-center" tag="h2">Update Information</Label>
        <br />
        <GridContainer width="300" gap="20">
          {updateInput("name", { title: "Name" })}
          {updateInput("email", { title: "Email", disabled: emailStatic })}
          {updateInput("phoneNumber", { title: "Phone Number", disabled: smsStatic })}
          {dateInput()}
          {updateInput("language", { title: "Language", options: localizations.filter(l => l.supported).map((l, i) => <option key={i} value={l.id}>{l.name}</option>) })}
          {updateInput("timezone", { title: "Timezone", options: timezoneList.map((t, i) => <option key={i} value={t}>{t}</option>) })}
        </GridContainer>
        <div className="pt-4 d-flex justify-content-between">
          <Button color="dark" onClick={handleCancel}>Cancel</Button>
          <Button color="green" onClick={handleSubmit}>Update</Button>
        </div>
      </Form>
    </CenterModal>
  );
};

export default ModalDataUpdader;
